import { z } from "zod"
import { createFormikValidate } from "../../../utils/formik"
import { useFormik } from "formik"
import { useMemo, useState } from "react"
import { useSaveBillingSettingsMutation } from "../../api"
import { useAuthUser } from "../../../users/hooks/use-auth-user"
import { useSnackbar } from "notistack"
import { unknownErrorToString } from "../../../utils/errors"
import { useTranslation } from "react-i18next"

export type BillingSettingsFormValues = z.infer<typeof BillingSettingsFormSchema>

export const BillingSettingsFormSchema = z.object({
  billingAddress: z.object({
    company: z.string().min(1),
    street: z.string().min(1),
    city: z.string().min(1),
    postalCode: z.string().min(1),
    country: z.string().min(1),
  }),
  contactPerson: z.object({
    firstname: z.string().min(1),
    lastname: z.string().min(1),
    email: z.string().min(1),
    phone: z.string().optional(),
    language: z.string().optional(),
  }),
  invoicingAddress: z.discriminatedUnion("invoicingMethod", [
    z.object({
      invoicingMethod: z.literal("email"),
      invoicingEmail: z.string().min(1),
      vatNumber: z.string().min(1),
      invoicingReferenceNumber: z.string().optional(),
    }),
    z.object({
      invoicingMethod: z.literal("eInvoice"),
      electronicInvoicingAddress: z.string().min(1),
      invoicingOperatorId: z.string().min(1),
      vatNumber: z.string().min(1),
      invoicingReferenceNumber: z.string().optional(),
    }),
  ]),
  agreeToPolicy: z.boolean(),
})

export const useBillingSettingsForm = ({
  orgId,
  initialValues,
  onSuccess,
}: {
  orgId: string
  initialValues: BillingSettingsFormValues
  onSuccess?: () => void
}) => {
  const { user } = useAuthUser()
  const [saveBillingSettings, meta] = useSaveBillingSettingsMutation()
  const [isContactPersonAutoFilled, setIsContactPersonAutoFilled] = useState(false)
  const [isInvoicingEmailAutoFilled, setIsInvoicingEmailAutoFilled] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation("org")

  const formik = useFormik({
    initialValues,
    validate: createFormikValidate(BillingSettingsFormSchema),
    onSubmit: async (values) => {
      try {
        await saveBillingSettings({ orgId, ...values }).unwrap()
        enqueueSnackbar(t("orgBillingPage.billingSettingsSaved"), { variant: "success" })
        if (onSuccess) onSuccess()
      } catch (error) {
        enqueueSnackbar(unknownErrorToString(error), { variant: "error" })
      }
    },
  })

  const hasAllRequiredFields = useMemo(() => {
    const safeParsedValues = BillingSettingsFormSchema.safeParse(formik.values)
    return safeParsedValues.success
  }, [formik.values])

  const autoFillContactPerson = () => {
    const contactPerson = {
      firstname: user?.firstname || formik.values.contactPerson.firstname,
      lastname: user?.lastname || formik.values.contactPerson.lastname,
      email: user?.email || formik.values.contactPerson.email,
      phone: formik.values.contactPerson.phone,
      language: user?.language || formik.values.contactPerson.language,
    }
    formik.setFieldValue("contactPerson", contactPerson)
    setIsContactPersonAutoFilled(true)
  }

  const resetContactPersonAutoFill = () => {
    formik.setFieldValue("contactPerson", {
      firstname: "",
      lastname: "",
      email: "",
      phone: formik.values.contactPerson.phone,
      language: "",
    })
    setIsContactPersonAutoFilled(false)
  }

  const autoFillInvoicingEmail = () => {
    formik.setFieldValue("invoicingAddress.invoicingEmail", user?.email || "")
    setIsInvoicingEmailAutoFilled(true)
  }

  const handleContactPersonFieldChange = (e: React.ChangeEvent<any>) => {
    setIsContactPersonAutoFilled(false)
    formik.handleChange(e)
  }

  const handleInvoicingEmailChange = (e: React.ChangeEvent<any>) => {
    setIsInvoicingEmailAutoFilled(false)
    formik.handleChange(e)
  }

  const setInvoicingMethod = (method: "email" | "eInvoice") => {
    setIsInvoicingEmailAutoFilled(false)
    formik.setFieldValue("invoicingAddress.invoicingMethod", method)

    if (method === "email") {
      formik.setFieldValue("invoicingAddress.electronicInvoicingAddress", "")
      formik.setFieldValue("invoicingAddress.invoicingOperatorId", "")
    } else {
      formik.setFieldValue("invoicingAddress.invoicingEmail", "")
    }
  }

  return {
    formState: formik,
    isAgreeToPolicyDisabled: !hasAllRequiredFields,
    hasAllRequiredFields,
    saveBillingSettings,
    meta,
    autoFillContactPerson,
    resetContactPersonAutoFill,
    isContactPersonAutoFilled,
    setIsContactPersonAutoFilled,
    handleContactPersonFieldChange,
    autoFillInvoicingEmail,
    isInvoicingEmailAutoFilled,
    handleInvoicingEmailChange,
    setIsInvoicingEmailAutoFilled,
    setInvoicingMethod,
  }
}
